/**
 * Organisation, employee, product, dev-permissions, dev-adjustment typed fetch helpers.
 */
import type { AccessAudienceResponse, AccessPageRuleRow, PageRulesSetRequest, AccessProfile, ApiResponse, EcodeListSetRequest, AppPermissionMatrix, CheckProductCodeResponse, CreateEmployeeInput, CreateProductInput, DeclareProductInput, DeclareProductUpdateInput, DeclaredProductInfo, DevAdjMonthEditor, DevAdjProduct, DevAdjustmentLogRecord, DevAdjustmentRecord, DevPermissionRecord, EmployeeRecord, IapEstimateSetting, LookupSets, MonetAssignmentHistoryEntry, MonetAssignmentsResponse, MonetEditorAllowlistResponse, ProductAdmobHistoryRecord, ProductOptionRecord, ProductRecord, SalaryRecord, SessionUser, UaAssignmentRecord, UpdateEmployeeInput, UpdateProductInput, UpsertDevAdjustmentInput, UpsertDevPermissionInput } from '@system/shared'
import { apiFetch } from './api-client-core'

/** GET /api/me — returns the authenticated user; 401 if no session. */
export async function getMe(): Promise<ApiResponse<SessionUser>> {
  return apiFetch<SessionUser>('/api/me')
}

/** GET /api/access — caller RBAC profile (Developer-only restriction + max history window). */
export async function getAccess(): Promise<ApiResponse<AccessProfile>> {
  return apiFetch<AccessProfile>('/api/access')
}

/** PUT /api/access/nav-toggles — replace the disabled sidebar paths (SPN004 only, 403 otherwise). */
export async function saveNavToggles(disabled: string[]): Promise<ApiResponse<string[]>> {
  return apiFetch<string[]>('/api/access/nav-toggles', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ disabled }),
  })
}

/** GET /api/access/audience — everyone who can sign in, with their effective profile (SPN004 only). */
export async function getAccessAudience(): Promise<ApiResponse<AccessAudienceResponse>> {
  return apiFetch<AccessAudienceResponse>('/api/access/audience')
}

/** PUT /api/access/page-rules — every person/department rule change for one page, atomically (SPN004 only). */
export async function setPageRules(body: PageRulesSetRequest): Promise<ApiResponse<AccessPageRuleRow[]>> {
  return apiFetch<AccessPageRuleRow[]>('/api/access/page-rules', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
}

/** PUT /api/access/ecode-lists — replace one editable eCode list (SPN004 only). */
export async function setEcodeList(body: EcodeListSetRequest): Promise<ApiResponse<string[]>> {
  return apiFetch<string[]>('/api/access/ecode-lists', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
}

/** GET /api/employees — full employee records including PII (auth required). */
export async function getEmployees(): Promise<ApiResponse<EmployeeRecord[]>> {
  return apiFetch<EmployeeRecord[]>('/api/employees')
}

/** GET /api/employees/me — the signed-in user's own employee record (any employee). */
export async function getEmployeeMe(): Promise<ApiResponse<EmployeeRecord | null>> {
  return apiFetch<EmployeeRecord | null>('/api/employees/me')
}

/** GET /api/employees/me/salary — the signed-in user's own salary record (any employee). */
export async function getEmployeeMySalary(): Promise<ApiResponse<SalaryRecord | null>> {
  return apiFetch<SalaryRecord | null>('/api/employees/me/salary')
}

/** POST /api/employees — create a new employee (Postgres-only, auth required). */
export async function createEmployee(input: CreateEmployeeInput): Promise<ApiResponse<EmployeeRecord>> {
  return apiFetch<EmployeeRecord>('/api/employees', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
}

/** PUT /api/employees/:eCode — update an existing employee (auth required). */
export async function updateEmployee(eCode: string, input: UpdateEmployeeInput): Promise<ApiResponse<EmployeeRecord>> {
  return apiFetch<EmployeeRecord>(`/api/employees/${encodeURIComponent(eCode)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
}

/** GET /api/lookups — all lookup sets (departments, titles, levels, types, statuses). */
export async function getLookups(): Promise<ApiResponse<LookupSets>> {
  return apiFetch<LookupSets>('/api/lookups')
}

/** GET /api/employees/salary — all salary records joined with employee name (auth required). */
export async function getSalaries(): Promise<ApiResponse<SalaryRecord[]>> {
  return apiFetch<SalaryRecord[]>('/api/employees/salary')
}

/** GET /api/products — all product records (auth required). */
export async function getProducts(): Promise<ApiResponse<ProductRecord[]>> {
  return apiFetch<ProductRecord[]>('/api/products')
}

/** PUT /api/products/:pCode — update a product (Postgres-only, auth required). */
export async function updateProduct(pCode: string, input: UpdateProductInput): Promise<ApiResponse<ProductRecord>> {
  return apiFetch<ProductRecord>(`/api/products/${encodeURIComponent(pCode)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
}

/** POST /api/products — create a product; writes through the erp master then mirrors (admin). */
export async function createProduct(input: CreateProductInput): Promise<ApiResponse<ProductRecord>> {
  return apiFetch<ProductRecord>('/api/products', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
}

/** POST /api/products/declare — dev self-declares a product (server forces PENDING / pDev=Long). */
export async function declareProduct(input: DeclareProductInput): Promise<ApiResponse<ProductRecord>> {
  return apiFetch<ProductRecord>('/api/products/declare', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
}

/** GET /api/products/declared/:pCode — declare-scope product view (+ canEdit flag). */
export async function getDeclaredProduct(pCode: string): Promise<ApiResponse<DeclaredProductInfo>> {
  return apiFetch<DeclaredProductInfo>(`/api/products/declared/${encodeURIComponent(pCode)}`)
}

/** PUT /api/products/declared/:pCode — declare-scope update by the product's dev (or admin). */
export async function updateDeclaredProduct(
  pCode: string,
  input: DeclareProductUpdateInput,
): Promise<ApiResponse<{ pCode: string }>> {
  return apiFetch<{ pCode: string }>(`/api/products/declared/${encodeURIComponent(pCode)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
}

/** GET /api/products/options — minimal {pCode,pName} picker list (dev-accessible). */
export async function getProductOptions(): Promise<ApiResponse<ProductOptionRecord[]>> {
  return apiFetch<ProductOptionRecord[]>('/api/products/options')
}

/** GET /api/products/check-code — case-insensitive pCode duplicate probe for create/declare forms. */
export async function checkProductCode(pCode: string): Promise<ApiResponse<CheckProductCodeResponse>> {
  return apiFetch<CheckProductCodeResponse>(`/api/products/check-code?pCode=${encodeURIComponent(pCode)}`)
}

/** DELETE /api/products/:pCode?hard= — soft (INACTIVE) or hard delete via erp master (admin). */
export async function deleteProduct(pCode: string, hard: boolean): Promise<ApiResponse<{ pCode: string }>> {
  return apiFetch<{ pCode: string }>(`/api/products/${encodeURIComponent(pCode)}${hard ? '?hard=true' : ''}`, { method: 'DELETE' })
}

/** POST /api/products/:pCode/change-admob — close old mapping, open new, update pAdmobID (admin). */
export async function changeProductAdmob(pCode: string, input: { newAdmobID: string; changeDate: string; note?: string }): Promise<ApiResponse<{ pCode: string }>> {
  return apiFetch<{ pCode: string }>(`/api/products/${encodeURIComponent(pCode)}/change-admob`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
}

/** GET /api/monet-assignments — apps + MONET staff + editor allowlist + canEdit (monet/admin). */
export async function getMonetAssignments(showInactive: boolean): Promise<ApiResponse<MonetAssignmentsResponse>> {
  return apiFetch<MonetAssignmentsResponse>(`/api/monet-assignments${showInactive ? '?showInactive=true' : ''}`)
}

/** GET /api/monet-assignments/history — recent assignment changes, newest first. */
export async function getMonetAssignmentHistory(pCode?: string): Promise<ApiResponse<MonetAssignmentHistoryEntry[]>> {
  return apiFetch<MonetAssignmentHistoryEntry[]>(`/api/monet-assignments/history${pCode ? `?pCode=${encodeURIComponent(pCode)}` : ''}`)
}

/** PUT /api/monet-assignments/:pCode — replace assignment (editor/admin). */
export async function saveMonetAssignment(pCode: string, eCodes: string[], reason?: string): Promise<ApiResponse<{ changed: boolean; eCodes: string[] }>> {
  return apiFetch<{ changed: boolean; eCodes: string[] }>(`/api/monet-assignments/${encodeURIComponent(pCode)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ eCodes, reason }),
  })
}

/** GET /api/monet-assignments/settings/allowlist — who may edit assignments (admin only). */
export async function getMonetEditorAllowlist(): Promise<ApiResponse<MonetEditorAllowlistResponse>> {
  return apiFetch<MonetEditorAllowlistResponse>('/api/monet-assignments/settings/allowlist')
}

/** POST /api/monet-assignments/settings/allowlist — replace the editor allowlist (admin only). */
export async function saveMonetEditorAllowlist(editorECodes: string[]): Promise<ApiResponse<MonetEditorAllowlistResponse>> {
  return apiFetch<MonetEditorAllowlistResponse>('/api/monet-assignments/settings/allowlist', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ editorECodes }),
  })
}

/** GET /api/products/admob-history — AdMob ID assignment periods per product (auth required). */
export async function getProductAdmobHistory(): Promise<ApiResponse<ProductAdmobHistoryRecord[]>> {
  return apiFetch<ProductAdmobHistoryRecord[]>('/api/products/admob-history')
}

/** GET /api/products/iap-estimate-setting — whether estimated IAP counts into reports (admin). */
export async function getIapEstimateSetting(): Promise<ApiResponse<IapEstimateSetting>> {
  return apiFetch<IapEstimateSetting>('/api/products/iap-estimate-setting')
}

/** PUT /api/products/iap-estimate-setting — flip it; the API records who. */
export async function setIapEstimateSetting(enabled: boolean): Promise<ApiResponse<IapEstimateSetting>> {
  return apiFetch<IapEstimateSetting>('/api/products/iap-estimate-setting', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ enabled }),
  })
}

/** GET /api/ua-assignments — all UA assignment history records (auth required). */
export async function getUaAssignments(): Promise<ApiResponse<UaAssignmentRecord[]>> {
  return apiFetch<UaAssignmentRecord[]>('/api/ua-assignments')
}

/** GET /api/dev-permissions — all dev permission entries (auth required). */
export async function getDevPermissions(): Promise<ApiResponse<DevPermissionRecord[]>> {
  return apiFetch<DevPermissionRecord[]>('/api/dev-permissions')
}

/** GET /api/dev-permissions/groups — distinct dev groups (Product.pDev) for the Group dropdown. */
export async function getDevGroups(): Promise<ApiResponse<string[]>> {
  return apiFetch<string[]>('/api/dev-permissions/groups')
}

/** PUT /api/dev-permissions/:email — create or update a dev permission entry. */
export async function upsertDevPermission(email: string, input: UpsertDevPermissionInput): Promise<ApiResponse<DevPermissionRecord>> {
  return apiFetch<DevPermissionRecord>(`/api/dev-permissions/${encodeURIComponent(email)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
}

/** DELETE /api/dev-permissions/:email — remove a dev permission entry. */
export async function deleteDevPermission(email: string): Promise<ApiResponse<{ email: string }>> {
  return apiFetch<{ email: string }>(`/api/dev-permissions/${encodeURIComponent(email)}`, { method: 'DELETE' })
}

/** GET /api/dev-permissions/app-matrix — Long products + dev employees for App Permissions tab. */
export async function getAppPermissionMatrix(): Promise<ApiResponse<AppPermissionMatrix>> {
  return apiFetch<AppPermissionMatrix>('/api/dev-permissions/app-matrix')
}

/** PUT /api/dev-permissions/app/:pCode — replace pDevInternal for a Long product. */
export async function updateAppPermission(
  pCode: string,
  ecodes: string[],
): Promise<ApiResponse<string[]>> {
  return apiFetch<string[]>(`/api/dev-permissions/app/${encodeURIComponent(pCode)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ecodes }),
  })
}

/** PUT /api/dev-adjustment — upsert one DevAdjustment entry. */
export async function upsertDevAdjustment(input: UpsertDevAdjustmentInput): Promise<ApiResponse<DevAdjustmentRecord>> {
  return apiFetch<DevAdjustmentRecord>('/api/dev-adjustment', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
}

/** DELETE /api/dev-adjustment/:id — delete a DevAdjustment entry by composite id. */
export async function deleteDevAdjustment(id: string): Promise<ApiResponse<{ id: string }>> {
  return apiFetch<{ id: string }>(`/api/dev-adjustment/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

/** GET /api/dev-adjustment/dev-products — products with pDev set (for editor product list). */
export async function getDevAdjProducts(): Promise<ApiResponse<DevAdjProduct[]>> {
  return apiFetch<DevAdjProduct[]>('/api/dev-adjustment/dev-products')
}

/** GET /api/dev-adjustment/month?month=YYYY-MM&pCode=X — month editor data for one product. */
export async function getDevAdjMonth(month: string, pCode: string): Promise<ApiResponse<DevAdjMonthEditor>> {
  const params = new URLSearchParams()
  params.set('month', month)
  params.set('pCode', pCode)
  return apiFetch<DevAdjMonthEditor>(`/api/dev-adjustment/month?${params.toString()}`)
}

/** GET /api/dev-adjustment/months — distinct YYYY-MM present in DailyProfit, desc. */
export async function getDevAdjMonths(): Promise<ApiResponse<string[]>> {
  return apiFetch<string[]>('/api/dev-adjustment/months')
}

/** GET /api/dev-adjustment/logs — audit trail (newest first), optional ?refKey filter. */
export async function getDevAdjLogs(refKey?: string): Promise<ApiResponse<DevAdjustmentLogRecord[]>> {
  const qs = refKey ? `?refKey=${encodeURIComponent(refKey)}` : ''
  return apiFetch<DevAdjustmentLogRecord[]>(`/api/dev-adjustment/logs${qs}`)
}
